/**
 * CategoryRedirect — bridges the marketing landing page's chapter URLs
 * to the real routes served by our SPA.
 *
 * The MyFamilyTree.care marketing landing (a separate Emergent project)
 * links to URLs like `/category/family-tree`, `/category/celebrate-a-life`,
 * `/cemeteries`, etc. Those routes don't exist inside the SPA. This
 * component intercepts them and does one of:
 *
 *   1. If the visitor is on `myfamilytree.care`, redirect out to the
 *      corresponding page on `indianfamilytree.com` (per the founder's
 *      chosen integration model — landing lives on .care, functional
 *      app lives on IFT). External redirect via `window.location`.
 *   2. On any other host (socialpulse.com, indianfamilytree.com,
 *      preview URL), do an in-app <Navigate> to the equivalent internal
 *      route so QA + editors can hit the same URLs during development.
 */
import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";

// Map every landing chapter → the canonical internal route it should
// open. Adding a new wing? Add one row here and it just works.
const CATEGORY_ROUTES = {
  "family-tree":         "/family-trees",
  "family-trees":        "/family-trees",
  "celebrate-a-life":    "/celebrate",
  "memorials":           "/celebrate",
  "obituaries":          "/obituaries",
  "heritage":            "/heritage",
  "family-history":      "/family-tree-guide",
  "family-stories":      "/family-tree-guide",
  "genealogy":           "/family-tree-guide",
  // Placeholders — the following wings will get their own dedicated
  // routes in Phase 3 / Phase 4. Until then they land on the closest
  // existing page so links from the landing never 404.
  "historical-families": "/heritage",
  "local-history":       "/heritage",
};

function isMyFamilyTreeCareHost() {
  try {
    const h = (window.location.hostname || "").toLowerCase();
    return h === "myfamilytree.care" || h === "www.myfamilytree.care";
  } catch {
    return false;
  }
}

export default function CategoryRedirect() {
  const { slug } = useParams();
  const target = CATEGORY_ROUTES[(slug || "").toLowerCase()] || "/celebrate";

  useEffect(() => {
    if (isMyFamilyTreeCareHost()) {
      // External redirect — the .care domain hosts only the marketing
      // landing; the functional SPA lives on indianfamilytree.com.
      const url = `https://indianfamilytree.com${target}`;
      window.location.replace(url);
    }
  }, [target]);

  if (isMyFamilyTreeCareHost()) {
    // Placeholder while the browser hops to indianfamilytree.com.
    return (
      <div
        className="min-h-[40vh] grid place-items-center text-center px-6"
        data-testid="category-redirect-loader"
      >
        <div>
          <div className="overline mb-2">MYFAMILYTREE.CARE</div>
          <p className="font-display text-2xl">Opening the archive…</p>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Redirecting you to <b>indianfamilytree.com</b>.
          </p>
        </div>
      </div>
    );
  }

  return <Navigate to={target} replace data-testid="category-redirect" />;
}
